import { useState, useEffect, useRef } from 'react';
import { Heart, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import '@/App.css';
import success_1 from '@/assets/success_1.jpeg';

import Navigation from '@/components/nav';
import HeroSection from '@/components/HeroSection';
import FooterSection from '@/components/footer';

const API_BASE = import.meta.env.VITE_API_BASE || "https://qwaqwatalentshow.co.za";

interface CompetitionEvent {
  id: number;
  title: string;
  description: string;
  image: string | null;
  category: string;
  featured: number;
  active_participants: number;
  status: string;
  youtube_live_url: string | null;
  youtube_live_id: string | null;
  created_at: string;
  updated_at: string;
  is_read: number;
  close_date: string;
  close_time: string;
  registration_enabled: number;
  start_date: string;
  start_time: string;
}

interface EventsResponse {
  success: boolean;
  page: number;
  limit: number;
  count: number;
  total_participants: number;
  status: string;
  events: CompetitionEvent[];
}

// Events page
function Competitions() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [events, setEvents] = useState<CompetitionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 6;
  const [totalParticipants, setTotalParticipants] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/v1/events?page=${page}&limit=${limit}`);
        const data: EventsResponse = await res.json();
        if (data.success) {
          setEvents(data.events);
          setTotalPages(Math.ceil(data.count / limit));
          setTotalParticipants(data.total_participants);
        }
      } catch (err) {
        console.error("Failed to fetch events:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [page]);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection description="Competitions" long="Explore the exciting competitions happening now." />
      <Navigation />
      <main>
        <section id="competitions" ref={sectionRef} className="relative py-20 md:py-32 bg-gradient-to-b from-white to-gray-50 overflow-hidden">

          {/* Background Glow Effects */}
          <div className="absolute top-40 right-10 md:right-20 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/20 blur-3xl" />
          <div className="absolute bottom-20 left-10 w-60 h-60 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* HEADER */}
            <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="font-black text-3xl sm:text-4xl md:text-6xl text-QWA2_TALENT_SHOW-black leading-tight">
                COMPETITIONS <span className="bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold bg-clip-text text-transparent">LIST</span>
              </h2>
              <p className="mt-4 text-QWA2_TALENT_SHOW-gray text-base md:text-lg max-w-xl mx-auto">
                Explore the exciting competitions happening now.
              </p>
            </div>


           


{/* 🔥 STYLISH COMPETITIONS GRID */}
<div className="relative">

  {/* TOP STATS BAR */}
  <div
    className={`mb-10 transition-all duration-700 ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
    }`}
  >
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0f0f0f] p-6 md:p-8 shadow-[0_20px_80px_rgba(0,0,0,0.4)]">

      {/* Glow */}
      <div className="absolute -top-10 right-0 w-52 h-52 bg-QWA2_TALENT_SHOW-gold/20 blur-3xl rounded-full" />
      <div className="absolute bottom-0 left-0 w-40 h-40 bg-QWA2_TALENT_SHOW-red/20 blur-3xl rounded-full" />

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6">

        <div>
          <p className="text-white/60 text-sm uppercase tracking-[3px] mb-2">
            Total Competitions
          </p>

          <h3 className="text-4xl font-black text-white">
            {events.length}
          </h3>
        </div>

        <div>
          <p className="text-white/60 text-sm uppercase tracking-[3px] mb-2">
            Total Participants
          </p>

          <h3 className="text-4xl font-black bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold bg-clip-text text-transparent">
            {totalParticipants}
          </h3>
        </div>

        <div className="flex items-center justify-start md:justify-end">
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl">
            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white font-semibold text-sm">
              Live Competitions Running
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* BODY */}
  <div>

    {loading ? (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border-4 border-QWA2_TALENT_SHOW-gold/20"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-QWA2_TALENT_SHOW-gold animate-spin"></div>
        </div>

        <p className="mt-6 text-gray-400 font-medium tracking-wide">
          Loading amazing competitions...
        </p>
      </div>
    ) : events.length === 0 ? (
      <div className="relative overflow-hidden rounded-3xl border border-dashed border-gray-300 bg-white p-16 text-center">

        <div className="absolute top-0 right-0 w-40 h-40 bg-QWA2_TALENT_SHOW-gold/10 blur-3xl rounded-full" />

        <h3 className="text-2xl font-black text-gray-800">
          Nothing Here Yet
        </h3>

        <p className="mt-3 text-gray-500">
          Check back later for exciting competitions.
        </p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">

        {events.map((competition, index) => {

          const targetDate = new Date(
            `${competition.start_date}T${competition.start_time}`
          );

          const now = new Date();

          const diff = targetDate.getTime() - now.getTime();

          const isEnded = now.getTime() > targetDate.getTime();

          const days = Math.max(
            0,
            Math.floor(diff / (1000 * 60 * 60 * 24))
          );

          const hours = Math.max(
            0,
            Math.floor((diff / (1000 * 60 * 60)) % 24)
          );

          const minutes = Math.max(
            0,
            Math.floor((diff / (1000 * 60)) % 60)
          );

          return (

            <a
              href={`./competitions/${competition.id}`}
              key={competition.id}
              className={`group relative overflow-hidden rounded-[28px] transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >

              {/* MAIN CARD */}
              <div className="relative h-[470px] bg-[#111111] border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.45)] overflow-hidden">

                {/* IMAGE */}
                <div className="absolute inset-0 overflow-hidden">

                  <img
                    src={`${API_BASE}${competition.image || ''}`}
                    alt={competition.title}
                    className="w-full h-full object-cover transition-transform duration-1000"
                  />

                  {/* Overlay */}
                  {/* <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" /> */}
                </div>

                {/* TOP BADGES */}
                <div className="absolute top-5 left-5 right-5 z-20 flex items-start justify-between">

                  {/* CATEGORY */}
                  <div className="px-4 py-2 rounded-full backdrop-blur-xl bg-white/10 border border-white/10 text-white text-xs font-bold uppercase tracking-[2px]">
                    {competition.category}
                  </div>

                  {/* FEATURED */}
                  {competition.featured === 1 && (
                    <div className="px-4 py-2 rounded-full bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white text-xs font-black uppercase tracking-[2px] shadow-lg">
                      Featured
                    </div>
                  )}
                </div>

                {/* CONTENT */}
                <div className="relative z-10 flex flex-col justify-end h-full p-7">

                  {/* PARTICIPANTS */}
                  <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
                    <Users className="w-4 h-4" />
                    <span>
                      {competition.active_participants || 0} participants
                    </span>
                  </div>

                  {/* TITLE */}
                  <h3 className="text-2xl md:text-3xl font-black text-white leading-tight transition-colors duration-500">
                    {competition.title}
                  </h3>

                  {/* DESCRIPTION */}
                  <p className="mt-3 text-white/75 text-sm leading-relaxed line-clamp-3">
                    {competition.description}
                  </p>

                  {/* BOTTOM */}
                  <div className="mt-7 flex items-end justify-between">

                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-[2px] mb-1">
                        Competition Date
                      </p>

                      <p className="text-white font-semibold text-sm">
                        {new Date(competition.start_date).toLocaleDateString(
                          'en-GB',
                          {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          }
                        )}
                      </p>
                    </div>

                    {/* STATUS */}
                    {isEnded ? (
                      <div className="px-4 py-2 rounded-2xl bg-red-500/20 border border-red-400/30 backdrop-blur-xl">
                        <span className="text-red-300 text-xs font-black uppercase tracking-[2px]">
                          Ended
                        </span>
                      </div>
                    ) : (
                      <div className="text-right">

                        <p className="text-white/40 text-[10px] uppercase tracking-[2px] mb-2">
                          Starts In
                        </p>

                        <div className="flex items-center gap-2">

                          <div className="min-w-[55px] px-2 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/10">
                            <div className="text-white font-black text-sm">
                              {days}
                            </div>
                            <div className="text-[10px] text-white/50 uppercase">
                              Days
                            </div>
                          </div>

                          <div className="min-w-[55px] px-2 py-2 rounded-xl bg-QWA2_TALENT_SHOW-red/20 backdrop-blur-xl border border-QWA2_TALENT_SHOW-red/20">
                            <div className="text-white font-black text-sm">
                              {hours}
                            </div>
                            <div className="text-[10px] text-white/50 uppercase">
                              Hours
                            </div>
                          </div>

                          <div className="min-w-[55px] px-2 py-2 rounded-xl bg-QWA2_TALENT_SHOW-gold/20 backdrop-blur-xl border border-QWA2_TALENT_SHOW-gold/20">
                            <div className="text-white font-black text-sm">
                              {minutes}
                            </div>
                            <div className="text-[10px] text-white/50 uppercase">
                              Min
                            </div>
                          </div>

                        </div>
                      </div>
                    )}
                  </div>

                  {/* BUTTON */}
                  <div className="mt-7">

                    <div className="w-full py-4 rounded-2xl bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-center font-black text-white tracking-wide shadow-[0_10px_30px_rgba(255,180,0,0.3)] transition-all duration-500">
                      View Competition
                    </div>

                  </div>

                </div>

              </div>

            </a>
          );
        })}

      </div>
    )}

  </div>

  {/* PAGINATION */}
  {!loading && totalPages > 1 && (
    <div className="mt-16 flex flex-wrap items-center justify-center gap-4">

      {/* PREV */}
      <button
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={page === 1}
        className="group flex items-center gap-2 px-6 py-4 rounded-2xl bg-[#111111] border border-white/10 text-white font-bold hover:border-QWA2_TALENT_SHOW-gold/40 transition-all disabled:opacity-40"
      >
        <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
        Previous
      </button>

      {/* NUMBERS */}
      <div className="flex items-center gap-3">

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            className={`w-14 h-14 rounded-2xl text-sm font-black transition-all duration-300 ${
              page === i + 1
                ? 'bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white shadow-[0_10px_30px_rgba(255,180,0,0.4)] scale-110'
                : 'bg-[#111111] border border-white/10 text-white hover:border-QWA2_TALENT_SHOW-gold/30'
            }`}
          >
            {i + 1}
          </button>
        ))}

      </div>

      {/* NEXT */}
      <button
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
        className="group flex items-center gap-2 px-6 py-4 rounded-2xl bg-[#111111] border border-white/10 text-white font-bold hover:border-QWA2_TALENT_SHOW-gold/40 transition-all disabled:opacity-40"
      >
        Next
        <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
      </button>

    </div>
  )}
</div>

         



          </div>

        </section>
        <FooterSection />
      </main>
    </div>
  );
}

export default Competitions;
